<template>
<div class="sp-page">
  <div class="sp-toolbar no-print">
    <button class="btn btn-sm btn-success" @click="doPrint"><i class="mdi mdi-printer me-1"></i>Print / Save as PDF</button>
    <doc-share v-if="ss && !sharedView" class="ms-2" :title="'Slaughter Sheet — ' + (ss.farm_name || '')" :doc-ref="ss.document_ref" doc-type="slaughter_sheet" :doc-id="ss.id" />
    <button class="btn btn-sm btn-outline-secondary ms-2" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
  </div>

  <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

  <div v-else-if="ss" class="sp-doc">

    <!-- ======= SECTION 1: ARRIVAL + ANTE-MORTEM ======= -->
    <div class="sp-page-break">
      <header class="sp-head">
        <div class="sp-brand-block">
          <img src="/assets/images/flockr_logo.png" alt="Flockr" class="sp-brand-logo" />
          <div>
            <div class="sp-farm">{{ (ss.farm_name||'').toUpperCase() }}</div>
            <div class="sp-meta">{{ ss.farm_address || '' }}<span v-if="ss.farm_county"> — {{ ss.farm_county }} County, Kenya</span></div>
            <div v-if="ss.farm_phone" class="sp-meta">Tel: {{ ss.farm_phone }}</div>
          </div>
        </div>
        <div class="sp-title-block">
          <div class="sp-title">SLAUGHTER SHEET</div>
          <div class="sp-subtitle">Post-Mortem Record &amp; Carcass Weight Register</div>
          <div class="sp-ref">{{ ss.document_ref }}<span v-if="ss.delivery_note_ref"> · Ref: {{ ss.delivery_note_ref }}</span></div>
          <div class="sp-meta sp-meta-sm">Powered by <b>flockr.africa</b> · Generated {{ fmtDate(new Date().toISOString()) }}</div>
        </div>
      </header>

      <div class="sp-meta-grid">
        <div><span class="sp-l">Slaughter Date</span><strong>{{ fmtDate(ss.slaughter_date) }}</strong></div>
        <div><span class="sp-l">Abattoir</span><strong>{{ ss.abattoir_name }}</strong></div>
        <div><span class="sp-l">County</span><strong>{{ ss.farm_county || '—' }}</strong></div>
        <div><span class="sp-l">Total Animals</span><strong>{{ ss.items.length }}</strong></div>
        <div><span class="sp-l">Vet Officer</span><strong>{{ ss.vet_officer_name || '—' }}</strong></div>
        <div><span class="sp-l">Abattoir Reg. No.</span><strong>{{ ss.abattoir_reg_no || '—' }}</strong></div>
      </div>

      <h3 class="sp-section">A. Consignment arrival details</h3>
      <table class="sp-mini"><tr>
        <td><strong>Supplier / Ranch</strong><span class="sp-fill">{{ ss.farm_name }}</span></td>
        <td><strong>Supplier Address</strong><span class="sp-fill">{{ ss.farm_address || '' }}</span></td>
        <td><strong>DVS Movement Permit No.</strong><span class="sp-fill">{{ ss.dvs_permit_no || '' }}</span></td>
      </tr><tr>
        <td><strong>Transporter Name</strong><span class="sp-fill">{{ ss.transporter_name || '' }}</span></td>
        <td><strong>Vehicle Registration</strong><span class="sp-fill">{{ ss.vehicle_reg || '' }}</span></td>
        <td><strong>Arrival Date &amp; Time</strong><span class="sp-fill">{{ ss.arrival_at ? fmtDateTime(ss.arrival_at) : '' }}</span></td>
      </tr><tr>
        <td colspan="3"><strong>Holding Period (hours)</strong><span class="sp-fill">{{ ss.lairage_hours || '' }}</span></td>
      </tr></table>

      <h3 class="sp-section">B. Ante-mortem inspection register</h3>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>Class</th><th>Breed Type</th><th>Est. Age</th><th>Liveweight (kg)</th><th>AM Status</th><th>Slaughter date</th><th>Time</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in ss.items" :key="'am-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td><td>{{ it.animal_class || '—' }}</td><td>{{ it.breed_type || '—' }}</td>
            <td>{{ it.est_age_months ? it.est_age_months + ' mo' : '—' }}</td>
            <td class="num">{{ it.liveweight_kg ? Number(it.liveweight_kg).toFixed(1) + ' kg' : '—' }}</td>
            <td><span class="sp-pill" :class="'p-'+it.ante_mortem_status">{{ (it.ante_mortem_status||'').toUpperCase() }}</span></td>
            <td>{{ fmtDate(ss.slaughter_date) }}</td>
            <td>{{ it.slaughter_time || '—' }}</td>
          </tr>
        </tbody>
      </table>

      <div v-if="ss.ante_mortem_remarks" class="sp-remarks-block">
        <h4>D. Ante-mortem remarks</h4>
        <p>{{ ss.ante_mortem_remarks }}</p>
      </div>
    </div>

    <!-- ======= SECTION 2: CARCASS ======= -->
    <div class="sp-page-break">
      <header class="sp-head sp-head-thin">
        <div class="sp-farm-thin">{{ (ss.farm_name||'').toUpperCase() }}</div>
        <div class="sp-ref-thin">{{ ss.document_ref }}</div>
      </header>
      <h3 class="sp-section">Section 2 — Carcass weight, grading &amp; classification</h3>

      <h4 class="sp-h4">A. Carcass weight register</h4>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>LW (kg)</th><th>Hot CW (kg)</th><th>Cold CW (kg)</th><th>Dress %</th><th>Side L (kg)</th><th>Side R (kg)</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in ss.items" :key="'cw-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td>
            <td class="num">{{ fmt(it.liveweight_kg) }}</td>
            <td class="num">{{ fmt(it.hot_cw_kg) }}</td>
            <td class="num">{{ fmt(it.cold_cw_kg) }}</td>
            <td class="num">{{ it.dress_pct ? Number(it.dress_pct).toFixed(1) + '%' : '—' }}</td>
            <td class="num">{{ fmt(it.side_l_kg) }}</td>
            <td class="num">{{ fmt(it.side_r_kg) }}</td>
          </tr>
        </tbody>
        <tfoot><tr><td colspan="2"><strong>TOTALS</strong></td>
          <td class="num"><strong>{{ sumF('liveweight_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('hot_cw_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('cold_cw_kg') }}</strong></td>
          <td class="num"><strong>{{ avgF('dress_pct') }}%</strong></td>
          <td class="num"><strong>{{ sumF('side_l_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('side_r_kg') }}</strong></td>
        </tr></tfoot>
      </table>

      <h4 class="sp-h4">B. Carcass grading &amp; blemish register</h4>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>Age (mo)</th><th>Fat (1-5)</th><th>Muscle (1-5)</th><th>Bruise (site &amp; size, cm)</th><th>Trim loss (kg)</th><th>Remarks</th><th>Grade</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in ss.items" :key="'gr-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td>
            <td>{{ it.est_age_months || '—' }}</td>
            <td>{{ it.fat_score || '—' }}</td>
            <td>{{ it.muscle_score || '—' }}</td>
            <td>{{ it.bruise_location || '—' }}</td>
            <td class="num">{{ fmt(it.trim_loss_kg) }}</td>
            <td>{{ it.remarks || '—' }}</td>
            <td><span v-if="it.grade" class="sp-grade" :class="'g-'+it.grade">{{ it.grade }}</span><span v-else>—</span></td>
          </tr>
        </tbody>
      </table>

      <h4 class="sp-h4">C. Grading reference key</h4>
      <table class="sp-key">
        <tr><td><strong>Grade A — Prime</strong></td><td>Excellent muscle, fat cover 3-4, no blemishes. Suitable for export and premium retail.</td></tr>
        <tr><td><strong>Grade B — Choice</strong></td><td>Good muscle, fat cover 2-3. Minor blemishes acceptable. Hotel and domestic retail.</td></tr>
        <tr><td><strong>Grade C — Standard</strong></td><td>Adequate muscle, fat cover 1-2. Some blemishes. Wholesale butchery and institutions.</td></tr>
        <tr><td><strong>Grade D — Utility</strong></td><td>Poor muscle or excess fat. Major blemishes. Processing and manufacture only.</td></tr>
      </table>
    </div>

    <!-- ======= SECTION 3: OFFAL + PM ======= -->
    <div class="sp-page-break">
      <header class="sp-head sp-head-thin">
        <div class="sp-farm-thin">{{ (ss.farm_name||'').toUpperCase() }}</div>
        <div class="sp-ref-thin">{{ ss.document_ref }}</div>
      </header>
      <h3 class="sp-section">Section 3 — Offal register, post-mortem findings &amp; payment summary</h3>

      <h4 class="sp-h4">A. Offal weight register — part 1 (Hide to Kidneys)</h4>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>Hide</th><th>Liver</th><th>Tripe</th><th>Heart</th><th>Lungs</th><th>Kidneys</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in ss.items" :key="'of1-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td>
            <td class="num">{{ fmt(it.hide_kg) }}</td><td class="num">{{ fmt(it.liver_kg) }}</td>
            <td class="num">{{ fmt(it.tripe_kg) }}</td><td class="num">{{ fmt(it.heart_kg) }}</td>
            <td class="num">{{ fmt(it.lungs_kg) }}</td><td class="num">{{ fmt(it.kidneys_kg) }}</td>
          </tr>
        </tbody>
        <tfoot><tr><td colspan="2"><strong>TOTALS</strong></td>
          <td class="num"><strong>{{ sumF('hide_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('liver_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('tripe_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('heart_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('lungs_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('kidneys_kg') }}</strong></td>
        </tr></tfoot>
      </table>

      <h4 class="sp-h4">B. Offal weight register — part 2 (Head, Legs, Blood &amp; Totals)</h4>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>Head (kg)</th><th>Legs (kg)</th><th>Blood (L)</th><th>Other (kg)</th><th>Total offal (kg)</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in ss.items" :key="'of2-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td>
            <td class="num">{{ fmt(it.head_kg) }}</td><td class="num">{{ fmt(it.legs_kg) }}</td>
            <td class="num">{{ fmt(it.blood_l) }}</td><td class="num">{{ fmt(it.other_offal_kg) }}</td>
            <td class="num"><strong>{{ fmt(it.total_offal_kg) }}</strong></td>
          </tr>
        </tbody>
        <tfoot><tr><td colspan="2"><strong>TOTALS</strong></td>
          <td class="num"><strong>{{ sumF('head_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('legs_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('blood_l') }}</strong></td>
          <td class="num"><strong>{{ sumF('other_offal_kg') }}</strong></td>
          <td class="num"><strong>{{ sumF('total_offal_kg') }}</strong></td>
        </tr></tfoot>
      </table>

      <h4 class="sp-h4">C. Post-mortem findings register</h4>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>Liver</th><th>Lungs</th><th>Lymph nodes</th><th>Condemned</th><th>Vet initials</th><th>PM Status</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in ss.items" :key="'pm-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td>
            <td>{{ it.liver_condition || '—' }}</td>
            <td>{{ it.lungs_condition || '—' }}</td>
            <td>{{ it.lymph_nodes_condition || '—' }}</td>
            <td>{{ it.condemned_parts || 'NONE' }}</td>
            <td>{{ it.vet_initials || '—' }}</td>
            <td><span class="sp-pill" :class="'p-'+it.pm_status">{{ (it.pm_status||'').toUpperCase() }}</span></td>
          </tr>
        </tbody>
      </table>

      <h4 class="sp-h4">D. Consolidated carcass summary</h4>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>LW</th><th>CCW</th><th>Dress %</th><th>Grade</th><th>Offal</th><th>PM</th><th>Price/kg</th><th>Amount (KES)</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in ss.items" :key="'sum-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td>
            <td class="num">{{ fmt(it.liveweight_kg) }}</td>
            <td class="num">{{ fmt(it.cold_cw_kg) }}</td>
            <td class="num">{{ it.dress_pct ? Number(it.dress_pct).toFixed(1) + '%' : '—' }}</td>
            <td><span v-if="it.grade" class="sp-grade" :class="'g-'+it.grade">{{ it.grade }}</span><span v-else>—</span></td>
            <td class="num">{{ fmt(it.total_offal_kg) }}</td>
            <td><span class="sp-pill" :class="'p-'+it.pm_status">{{ (it.pm_status||'').toUpperCase() }}</span></td>
            <td class="num">{{ it.price_per_kg_kes ? 'KES '+Number(it.price_per_kg_kes).toLocaleString() : '—' }}</td>
            <td class="num"><strong>{{ it.amount_kes ? 'KES '+Number(it.amount_kes).toLocaleString() : '—' }}</strong></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ======= SECTION 4: VET DECLARATION ======= -->
    <div class="sp-page-break">
      <header class="sp-head sp-head-thin">
        <div class="sp-farm-thin">{{ (ss.farm_name||'').toUpperCase() }}</div>
        <div class="sp-ref-thin">{{ ss.document_ref }}</div>
      </header>
      <h3 class="sp-section">Section 4 — Veterinary declaration &amp; official sign-off</h3>

      <h4 class="sp-h4">A. Official post-mortem &amp; meat inspection certificate</h4>
      <div class="sp-cert">
        <strong>KENYA MEAT CONTROL ACT CAP. 356 — OFFICIAL MEAT INSPECTION CERTIFICATE</strong>
        <p>I, the undersigned Meat Inspector / Veterinary Officer authorised under the Kenya Meat Control Act (Cap. 356) and appointed by the {{ ss.farm_county || '__________' }} County Government, hereby certify that the {{ ss.items.length }} ({{ ss.items.length }}) animals described in this slaughter sheet, delivered under Delivery Note <strong>{{ ss.delivery_note_ref || ss.document_ref }}</strong> dated <strong>{{ fmtDate(ss.slaughter_date) }}</strong> from <strong>{{ ss.farm_name }}</strong>, have been subjected to ante-mortem and post-mortem inspection in accordance with the Act and applicable county regulations. All carcasses and offal passed for human consumption are wholesome and fit for the purpose stated. Any condemnations are recorded in the Condemnation Register below.</p>
      </div>

      <table class="sp-mini">
        <tr><td><strong>Meat Inspector Full Name</strong><br />{{ ss.meat_inspector_name || '' }}</td>
            <td><strong>DVS / County Vet Reg. No.</strong><br />{{ ss.meat_inspector_reg_no || '' }}</td>
            <td><strong>Inspection Date &amp; Time</strong><br />{{ fmtDate(ss.slaughter_date) }} {{ ss.signed_at ? fmtTime(ss.signed_at) : '__:__' }}</td></tr>
        <tr><td><strong>Abattoir Licence No.</strong><br />{{ ss.abattoir_licence_no || '' }}</td>
            <td><strong>Animals Passed (of {{ ss.items.length }})</strong><br />{{ ss.animals_passed != null ? ss.animals_passed : '—' }}</td>
            <td><strong>Animals / Parts Condemned</strong><br />{{ ss.animals_condemned || 0 }}</td></tr>
        <tr><td><strong>Total Weight Condemned (kg)</strong><br />{{ ss.total_weight_condemned_kg || '0' }}</td>
            <td><strong>Notifiable Disease Detected?</strong><br /><span v-if="ss.notifiable_disease == 1" style="color:#B71C1C;font-weight:700;">YES — see remarks</span><span v-else>NO</span></td>
            <td><strong>DVS Notification Ref.</strong><br />{{ ss.dvs_notification_ref || '—' }}</td></tr>
      </table>

      <h4 class="sp-h4">B. Condemnation register</h4>
      <table class="sp-data">
        <thead><tr><th>#</th><th>Ear Tag</th><th>Part / Organ Condemned</th><th>Reason</th><th>Wt (kg)</th><th>Disposal</th><th>Inspector Initials</th></tr></thead>
        <tbody>
          <tr v-for="(it, i) in condemnedItems" :key="'cd-'+it.id">
            <td>{{ i+1 }}</td><td><strong>{{ it.ear_tag }}</strong></td><td>{{ it.condemned_parts || 'WHOLE CARCASS' }}</td>
            <td>{{ it.condemn_reason || '—' }}</td>
            <td class="num">{{ fmt(it.trim_loss_kg) }}</td>
            <td>Incinerated</td>
            <td>{{ it.vet_initials || '—' }}</td>
          </tr>
          <tr v-if="!condemnedItems.length"><td colspan="7" style="text-align:center;color:#777;padding:14px;">No condemnations</td></tr>
        </tbody>
      </table>

      <h4 class="sp-h4">C. Authorisations &amp; signatures</h4>
      <div class="sp-sign-grid">
        <div class="sp-sign">
          <div class="sp-sign-role">Ranch Representative</div>
          <div class="sp-sign-sub">Supplier / Owner</div>
          <div class="sp-sig-slot">{{ ss.ranch_signed_name || '&nbsp;' }}</div>
          <div class="sp-sig-label">Name &amp; Signature · {{ fmtDate(ss.slaughter_date) }}</div>
        </div>
        <div class="sp-sign">
          <div class="sp-sign-role">Abattoir Manager</div>
          <div class="sp-sign-sub">Facility Management</div>
          <div class="sp-sig-slot">{{ ss.abattoir_signed_name || '&nbsp;' }}</div>
          <div class="sp-sig-label">Name &amp; Signature · Date</div>
        </div>
        <div class="sp-sign">
          <div class="sp-sign-role">Meat Inspector / Vet</div>
          <div class="sp-sign-sub">Official Stamp Required</div>
          <div class="sp-sig-slot">{{ ss.inspector_signed_name || '&nbsp;' }}</div>
          <div class="sp-sig-label">Name · Stamp &amp; Date</div>
        </div>
        <div class="sp-sign">
          <div class="sp-sign-role">Buyer / Offtaker</div>
          <div class="sp-sign-sub">Representative</div>
          <div class="sp-sig-slot">{{ ss.buyer_signed_name || '&nbsp;' }}</div>
          <div class="sp-sig-label">Name &amp; Signature · Date</div>
        </div>
      </div>

      <div class="sp-retain">
        <strong>Record Retention — Distribution Notice</strong>
        <p>This slaughter sheet is an official production and inspection record under the Kenya Meat Control Act (Cap. 356) and must be retained for a minimum of three (3) years from date of slaughter. Distribution: Original — Abattoir | Copy 1 — Supplier | Copy 2 — County Vet | Copy 3 — Buyer.</p>
        <div class="sp-retain-grid">
          <div><span class="sp-l">Document Reference</span><strong>{{ ss.document_ref }}</strong></div>
          <div><span class="sp-l">Abattoir File Ref.</span><div class="sp-sig-slot sp-sig-inline">&nbsp;</div></div>
          <div><span class="sp-l">County Vet File Ref.</span><div class="sp-sig-slot sp-sig-inline">&nbsp;</div></div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
.sp-page { background:#F0EDE5; min-height:100vh; padding:24px 16px; color:#1A1A1A; font-family:Arial, sans-serif; }
.sp-toolbar { max-width:1100px; margin:0 auto 14px; display:flex; justify-content:center; }
.sp-doc { max-width:1100px; margin:0 auto; }
.sp-page-break { background:#FFFFFF; border:1px solid #D8D2C2; padding:28px 36px; box-shadow:0 4px 16px rgba(0,0,0,.06); margin-bottom:14px; }

.sp-head { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:12px; border-bottom:2px solid #1A1A1A; margin-bottom:14px; gap:20px; }
.sp-brand-block { display:flex; gap:14px; align-items:flex-start; }
.sp-brand-logo { width:52px; height:52px; object-fit:contain; flex-shrink:0; }
.sp-meta-sm { font-size:9px; color:#8A827A; margin-top:4px; }
.sp-fill { display:block; margin-top:4px; padding:4px 6px; min-height:16px; border-bottom:1px solid #BFA164; font-family:'Courier New',monospace; font-size:11px; color:#1A1A1A; }
.sp-sig-slot { min-height:34px; margin-top:6px; margin-bottom:4px; padding:6px 4px; border-bottom:1px solid #1A1A1A; font-family:'Courier New',monospace; font-size:11px; color:#1A1A1A; }
.sp-sig-slot.sp-sig-inline { min-height:20px; margin-top:2px; padding:2px 4px; font-size:10px; }
.sp-sig-label { font-size:9px; color:#888; text-align:center; text-transform:uppercase; letter-spacing:.5px; }
.sp-head-thin { padding-bottom:8px; border-bottom:1px solid #D8D2C2; }
.sp-farm { font-family:Georgia,serif; color:#1A1A1A; font-size:18px; font-weight:700; letter-spacing:.5px; }
.sp-farm-thin { font-family:Georgia,serif; color:#5C4533; font-size:14px; font-weight:700; }
.sp-meta { font-size:10px; color:#666; margin-top:2px; }
.sp-title-block { text-align:right; }
.sp-title { font-family:Georgia,serif; color:#1A1A1A; font-size:18px; letter-spacing:2px; font-weight:700; }
.sp-subtitle { font-size:10px; color:#888; letter-spacing:1px; }
.sp-ref { font-family:'Courier New',monospace; color:#555; font-size:10px; margin-top:4px; }
.sp-ref-thin { font-family:'Courier New',monospace; color:#888; font-size:10px; }

.sp-meta-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px 18px; background:#F7F5F0; padding:10px 14px; border:1px solid #E0DDD3; border-radius:3px; margin-bottom:14px; }
.sp-meta-grid > div { display:flex; flex-direction:column; }
.sp-l { font-size:9px; color:#888; text-transform:uppercase; letter-spacing:1px; }
.sp-meta-grid strong { font-size:11px; color:#1A1A1A; }

.sp-section { font-family:Georgia,serif; font-size:13px; letter-spacing:1.5px; color:#1A1A1A; background:#F7F5F0; padding:7px 12px; border-left:3px solid #1A1A1A; margin:16px 0 10px; }
.sp-h4 { font-family:Georgia,serif; font-size:11px; letter-spacing:1px; color:#5C4533; margin:14px 0 6px; padding-bottom:3px; border-bottom:1px dotted #D8D2C2; }

.sp-data { width:100%; border-collapse:collapse; font-size:10px; }
.sp-data thead th { background:#1A1A1A; color:#FFF; padding:6px 5px; text-align:left; font-size:9px; letter-spacing:.5px; font-weight:700; }
.sp-data tbody td { padding:5px 5px; border-bottom:1px solid #E5E0D5; vertical-align:middle; }
.sp-data tfoot td { padding:5px; background:#F7F5F0; border-top:2px solid #1A1A1A; }
.sp-data .num { text-align:right; font-family:'Courier New',monospace; }

.sp-mini { width:100%; border-collapse:collapse; font-size:10px; margin:8px 0 14px; }
.sp-mini td { padding:8px 10px; border:1px solid #E0DDD3; vertical-align:top; }
.sp-mini td strong { font-size:9px; color:#888; text-transform:uppercase; letter-spacing:.8px; display:block; margin-bottom:3px; }

.sp-key { width:100%; border-collapse:collapse; font-size:10px; }
.sp-key td { padding:6px 10px; border-bottom:1px solid #E5E0D5; }
.sp-key td:first-child { width:140px; }

.sp-pill { padding:1px 6px; border-radius:2px; font-size:8px; letter-spacing:.5px; font-weight:700; }
.sp-pill.p-pending { background:#E0DDD3; color:#777; }
.sp-pill.p-pass { background:#C8E6C9; color:#1F6B1F; }
.sp-pill.p-passed { background:#C8E6C9; color:#1F6B1F; }
.sp-pill.p-hold { background:#FFE0B2; color:#8B5A2B; }
.sp-pill.p-reject { background:#FFCDD2; color:#B71C1C; }
.sp-pill.p-failed { background:#FFCDD2; color:#B71C1C; }
.sp-pill.p-partial { background:#FFE0B2; color:#8B5A2B; }

.sp-grade { font-family:Georgia,serif; font-weight:700; padding:1px 5px; border-radius:2px; font-size:11px; }
.sp-grade.g-A { background:#C8E6C9; color:#1F6B1F; }
.sp-grade.g-B { background:#D6E9FF; color:#0F4C81; }
.sp-grade.g-C { background:#FFE0B2; color:#8B5A2B; }
.sp-grade.g-D { background:#FFCDD2; color:#B71C1C; }

.sp-remarks-block { margin-top:14px; padding:10px 12px; background:#F7F5F0; border-left:3px solid #5C4533; }
.sp-remarks-block h4 { font-size:11px; margin:0 0 6px; color:#5C4533; }
.sp-remarks-block p { margin:0; font-size:10px; color:#1A1A1A; line-height:1.5; }

.sp-cert { background:#F7F5F0; border:1px solid #E0DDD3; padding:12px 16px; margin:8px 0 14px; font-size:10px; line-height:1.55; }
.sp-cert strong { display:block; margin-bottom:6px; letter-spacing:.5px; font-size:11px; }
.sp-cert p { margin:0; color:#1A1A1A; }

.sp-sign-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:14px; margin:10px 0; }
.sp-sign { border:1px solid #E0DDD3; padding:10px 14px; font-family:'Courier New',monospace; font-size:10px; }
.sp-sign-role { font-size:11px; font-weight:700; color:#1A1A1A; margin-bottom:2px; }
.sp-sign-sub { font-size:9px; color:#888; margin-bottom:8px; }
.sp-sign-line { margin-bottom:4px; }

.sp-retain { margin-top:14px; padding:12px 14px; background:#F7F5F0; border-left:3px solid #1A1A1A; font-size:10px; line-height:1.5; }
.sp-retain strong { display:block; margin-bottom:4px; font-size:11px; }
.sp-retain p { margin:0 0 8px; color:#5C4533; }
.sp-retain-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.sp-retain-grid > div { display:flex; flex-direction:column; }
.sp-retain-grid strong { font-size:10px; color:#1A1A1A; }

@media print {
  .no-print { display:none !important; }
  .sp-page { background:#fff; padding:0; }
  .sp-doc { max-width:none; }
  .sp-page-break { box-shadow:none; border:none; page-break-after:always; padding:0 !important; margin-bottom:0 !important; }
  .sp-page-break:last-child { page-break-after:auto; }
  /* Keep title tables + signature grids from splitting across pages */
  .sp-mini, .sp-cert, .sp-sign-grid, .sp-sign, .sp-retain, .sp-meta-grid, .sp-remarks-block { page-break-inside:avoid; }
  .sp-data thead { display:table-header-group; }
  .sp-data tr { page-break-inside:avoid; }
  /* Kill any parent scrollers on print */
  html, body { background:#fff !important; overflow:visible !important; }
}
@page { size: A4; margin: 12mm 10mm; }

/* On-screen mobile preview */
@media (max-width: 768px) {
  .sp-page { padding: 10px; }
  .sp-doc { padding: 12px; border-radius: 6px; }
  .sp-doc h1, .sp-doc h2, .sp-doc h3 { font-size: 15px; }
  .sp-table { font-size: 9.5px; }
  .sp-table td, .sp-table th { padding: 3px 4px; }
  .sp-page-break { padding: 12px; box-shadow: 0 1px 4px rgba(0,0,0,.1); }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
import DocShare from "@/components/flockr-farm/DocShare.vue";
export default {
  name: "SlaughterSheetPrint",
  components: { DocShare },
  data(){ return { loading:true, ss:null }; },
  computed:{
    condemnedItems(){ return this.ss ? (this.ss.items || []).filter(function(i){return i.pm_status==='failed' || i.pm_status==='partial' || (i.condemned_parts && i.condemned_parts !== 'NONE');}) : []; },
    sharedView(){ return !!this.$route.query.t; }
  },
  mounted(){
    var token = this.$route.query.t;
    if (token) this.loadByToken(token);
    else this.load(this.$route.params.id);
  },
  methods:{
    fmt(v){ if (v === null || v === undefined || v === '' || isNaN(Number(v))) return '—'; return Number(v).toFixed(1) + ' kg'; },
    sumF(f){ if (!this.ss) return 0; var s = 0; this.ss.items.forEach(function(i){ s += Number(i[f]) || 0; }); return Math.round(s * 100) / 100; },
    avgF(f){ if (!this.ss) return 0; var v = this.ss.items.map(function(i){ return Number(i[f]) || 0; }).filter(function(x){return x>0;}); if (!v.length) return 0; var s = v.reduce(function(a,b){return a+b;},0); return Math.round(s/v.length * 10)/10; },
    fmtDate(d){ if(!d)return ''; try { var x = new Date(d); var p = function(n){return n<10?'0'+n:n}; return p(x.getDate())+' / '+p(x.getMonth()+1)+' / '+x.getFullYear(); } catch(e){ return d; } },
    fmtDateTime(d){ if(!d)return ''; try { return new Date(d).toLocaleString(); } catch(e){ return d; } },
    fmtTime(d){ if(!d)return ''; try { return new Date(d).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}); } catch(e){ return ''; } },
    async load(id){
      this.loading = true;
      try {
        var r = await executeJson({ function:'getSlaughterSheet', slaughterSheetId:id }, flocker);
        if (r.data && r.data.success) this.ss = r.data.data.slaughterSheet;
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    async loadByToken(token){
      this.loading = true;
      try {
        var r = await executeJson({ function:'getSharedDocument', token: token }, flocker);
        if (r.data && r.data.success) this.ss = r.data.data.slaughterSheet;
        else alert((r.data && r.data.message) || 'This share link is no longer valid.');
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    doPrint(){ window.print(); }
  }
};
</script>
